import type { ReactElement } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { EditorialCardVariantId } from '@/components/carousel-editorial-card/bg-and-values-map';

export interface EditorialCardSkeletonProps {
    variant?: EditorialCardVariantId;
    paddingEnabledInSquareMode?: boolean;
    className?: string;
    backgroundColorClass?: string;
}

const variantStyles = {
    standard: {
        imageWrapper: 'aspect-[4/3] w-full',
        image: 'rounded-t-lg',
        content: 'gap-3 px-4 pb-4',
    },
    square: {
        imageWrapper: 'aspect-square w-full',
        image: 'rounded-lg',
        content: 'gap-3 p-0',
    },
    icon: {
        imageWrapper: 'size-20 shrink-0 overflow-hidden',
        image: 'rounded-none',
        content: 'gap-3 px-0 pb-0 items-center w-full',
    },
} as const satisfies Record<EditorialCardVariantId, { imageWrapper: string; image: string; content: string }>;

export default function EditorialCardSkeleton({
    className,
    variant = 'standard',
    paddingEnabledInSquareMode = false,
    backgroundColorClass = 'bg-sand-95',
}: EditorialCardSkeletonProps): ReactElement {
    const styles = variantStyles[variant];

    return (
        <div
            className={cn(
                'flex h-full w-full flex-col rounded-lg shadow-md',
                variant === 'icon' ? 'items-center gap-8 px-4 py-10' : 'gap-4',
                backgroundColorClass,
                className
            )}
            aria-hidden="true">
            {/* Image Skeleton */}
            <div
                className={cn(
                    'relative overflow-hidden',
                    styles.imageWrapper,
                    variant === 'square' && paddingEnabledInSquareMode && 'p-4'
                )}>
                <Skeleton className={cn('h-full w-full', styles.image)} />
            </div>

            {/* Content Skeleton */}
            <div
                className={cn(
                    'flex flex-1 flex-col',
                    styles.content,
                    paddingEnabledInSquareMode && variant === 'square' && 'px-4 pb-4'
                )}>
                {/* Subtitle & Title Blocks */}
                <div className={cn('flex flex-col gap-2', variant === 'icon' && 'items-center w-full')}>
                    {variant !== 'icon' && <Skeleton className="h-4 w-1/4" />} {/* Sottotitolo */}
                    <Skeleton className={cn('h-6', variant === 'icon' ? 'w-1/2' : 'w-3/4')} /> {/* Titolo */}
                </div>

                {/* Button/Link Skeleton */}
                <div className="mt-auto pt-2">
                    <Skeleton className={cn('h-10 w-28', variant === 'icon' && 'mx-auto')} />
                </div>
            </div>
        </div>
    );
}
